import type {
  CashierCanonicalMethodId,
  CashierCryptoAsset,
  CashierFlow,
  CashierMethodKind,
} from "../types";

const CASHIER_ASSET_ROOT = "/app/static/themes/monkeytilt/cashier";
const TRUST_ASSET_ROOT = "/app/static/themes/monkeytilt/auth/trust";
const CURRENCY_ASSET_ROOT = "/app/static/themes/monkeytilt/icons/currencies";

export type PaymentMethodDefinition = {
  id: CashierCanonicalMethodId;
  label: string;
  kind: CashierMethodKind;
  flows: readonly CashierFlow[];
  iconUrl: string | null;
  iconTreatment: "brand" | "dark" | "light" | "text";
};

export const PAYMENT_METHOD_REGISTRY: Readonly<
  Record<CashierCanonicalMethodId, PaymentMethodDefinition>
> = {
  apple_pay: {
    id: "apple_pay",
    label: "Apple Pay",
    kind: "apple-pay",
    flows: ["deposit", "withdraw"],
    iconUrl: `${TRUST_ASSET_ROOT}/applepay.svg`,
    iconTreatment: "dark",
  },
  googlepay: {
    id: "googlepay",
    label: "Google Pay",
    kind: "google-pay",
    flows: ["deposit"],
    iconUrl: `${TRUST_ASSET_ROOT}/googlepay.svg`,
    iconTreatment: "light",
  },
  card: {
    id: "card",
    label: "Card / Bank",
    kind: "card",
    flows: ["deposit", "withdraw"],
    iconUrl: `${CASHIER_ASSET_ROOT}/methods/card.svg`,
    iconTreatment: "brand",
  },
  bank: {
    id: "bank",
    label: "Bank",
    kind: "bank",
    flows: ["withdraw"],
    iconUrl: null,
    iconTreatment: "text",
  },
  paypal: {
    id: "paypal",
    label: "PayPal",
    kind: "paypal",
    flows: ["deposit", "withdraw"],
    iconUrl: `${CASHIER_ASSET_ROOT}/methods/paypal.svg`,
    iconTreatment: "light",
  },
  venmo: {
    id: "venmo",
    label: "Venmo",
    kind: "venmo",
    flows: ["deposit", "withdraw"],
    iconUrl: null,
    iconTreatment: "brand",
  },
  cashapp: {
    id: "cashapp",
    label: "Cash App",
    kind: "cash-app",
    flows: ["deposit"],
    iconUrl: null,
    iconTreatment: "brand",
  },
  wire: {
    id: "wire",
    label: "Wire",
    kind: "wire",
    flows: ["deposit"],
    iconUrl: null,
    iconTreatment: "text",
  },
  interac: {
    id: "interac",
    label: "Interac e-Transfer",
    kind: "interac",
    flows: ["deposit"],
    iconUrl: null,
    iconTreatment: "text",
  },
  gateway: {
    id: "gateway",
    label: "Gateway IO",
    kind: "gateway",
    flows: ["deposit"],
    iconUrl: null,
    iconTreatment: "text",
  },
  crypto: {
    id: "crypto",
    label: "Crypto",
    kind: "crypto",
    flows: ["deposit", "withdraw"],
    iconUrl: `${CASHIER_ASSET_ROOT}/methods/crypto.svg`,
    iconTreatment: "dark",
  },
};

const METHOD_ALIASES: Readonly<Record<string, CashierCanonicalMethodId>> = {
  apple_pay: "apple_pay",
  applepay: "apple_pay",
  bank: "bank",
  card: "card",
  cash_app: "cashapp",
  cashapp: "cashapp",
  crypto: "crypto",
  gateway: "gateway",
  gateway_io: "gateway",
  google_pay: "googlepay",
  googlepay: "googlepay",
  interac: "interac",
  paypal: "paypal",
  venmo: "venmo",
  wire: "wire",
};

export function canonicalMethodId(
  methodId: string,
): CashierCanonicalMethodId | null {
  return METHOD_ALIASES[methodId.trim().toLowerCase()] ?? null;
}

export function paymentMethodDefinition(
  methodId: string,
): PaymentMethodDefinition | null {
  const canonical = canonicalMethodId(methodId);
  return canonical ? PAYMENT_METHOD_REGISTRY[canonical] : null;
}

const CRYPTO_ICON_BY_SYMBOL: Readonly<Record<string, string>> = {
  BCH: `${CASHIER_ASSET_ROOT}/crypto/bch.svg`,
  BNB: `${CASHIER_ASSET_ROOT}/crypto/bnb.svg`,
  BTC: `${TRUST_ASSET_ROOT}/btc.svg`,
  DOGE: `${CASHIER_ASSET_ROOT}/crypto/doge.svg`,
  ETH: `${TRUST_ASSET_ROOT}/eth.svg`,
  LTC: `${CASHIER_ASSET_ROOT}/crypto/ltc.svg`,
  SHIB: `${CASHIER_ASSET_ROOT}/crypto/shib.svg`,
  SOL: `${CURRENCY_ASSET_ROOT}/sol.svg`,
  TRX: `${CASHIER_ASSET_ROOT}/crypto/trx.svg`,
  USDC: `${CURRENCY_ASSET_ROOT}/usdc.svg`,
  USDT: `${TRUST_ASSET_ROOT}/usdt.svg`,
  XRP: `${CASHIER_ASSET_ROOT}/crypto/xrp.svg`,
};

export function cryptoIconUrl(symbol: string): string {
  return (
    CRYPTO_ICON_BY_SYMBOL[symbol.trim().toUpperCase()] ??
    PAYMENT_METHOD_REGISTRY.crypto.iconUrl!
  );
}

export type CashierCryptoAssetGroup = {
  symbol: string;
  iconUrl: string;
  networks: readonly CashierCryptoAsset[];
};

export function groupCryptoAssets(
  assets: readonly CashierCryptoAsset[],
  flow: CashierFlow,
): CashierCryptoAssetGroup[] {
  const groups = new Map<string, CashierCryptoAsset[]>();

  for (const asset of assets) {
    const enabled =
      flow === "deposit" ? asset.depositEnabled : asset.withdrawEnabled;
    const symbol = asset.symbol.trim().toUpperCase();
    if (!enabled || !symbol || !asset.network.trim()) continue;
    const group = groups.get(symbol) ?? [];
    group.push(asset);
    groups.set(symbol, group);
  }

  return Array.from(groups, ([symbol, networks]) => ({
    symbol,
    iconUrl: cryptoIconUrl(symbol),
    networks,
  }));
}
